import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { Phone, KeyRound, User, ArrowRight, Loader2, Mail, Lock, Store } from 'lucide-react';
import { apiErrorMessage } from '@/lib/api-error';

type AuthMode = 'phone' | 'email';
type Step = 'phone' | 'otp' | 'result';
type EmailStep = 'login' | 'register' | 'forgot' | 'result';

// Canned copy for the two account-status responses shared by every login
// factor (password, OTP — see backend's AssertStaffLoginAllowed). The API's
// own message text ("Your account is pending approval.", …) is English and
// meant for logs, not the UI — never shown to the user directly.
// Built per-render (inside the component) so they follow the current language.

/** True for the `{error, status?}` shape every LoginBlockedException/OtpRateLimitedException renders as — never the `{message, errors}` shape a ValidationException renders as. */
function isApiErrorMessage(value: unknown): value is { error: string; status?: string } {
  return typeof value === 'object' && value !== null && typeof (value as { error?: unknown }).error === 'string';
}

export default function Login() {
  const { t } = useTranslation();
  const PENDING_MESSAGE = t('misc_login.pending');
  const BLOCKED_MESSAGE = t('misc_login.blocked');
  const RATE_LIMITED_MESSAGE = t('misc_login.rate_limited');
  const GENERIC_ERROR = t('misc_login.generic_error');
  const navigate = useNavigate();
  const { login, refreshUser } = useAuth();

  const [mode, setMode] = useState<AuthMode>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultType, setResultType] = useState<'success' | 'error' | 'pending'>('success');

  // Phone+OTP state
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('+998');
  const [otp, setOtp] = useState('');
  const [fullName, setFullName] = useState('');

  // Email auth state
  const [emailStep, setEmailStep] = useState<EmailStep>('login');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailPasswordConfirmation, setEmailPasswordConfirmation] = useState('');
  const [emailFullName, setEmailFullName] = useState('');
  const [merchantName, setMerchantName] = useState('');

  // Phone OTP handlers — FR-96 (Lovable verify-otp): an unknown phone with a
  // valid code registers a new owner (backend: AuthenticateStaffByOtp).
  const handleSendOtp = async () => {
    if (!/^\+998\d{9}$/.test(phone)) {
      setError(t('misc_login.phone_format'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: otpError, response } = await apiClient.POST('/otp/send', { body: { phone } });
      if (otpError) {
        setError(response.status === 429 ? RATE_LIMITED_MESSAGE : apiErrorMessage(otpError, GENERIC_ERROR));
        return;
      }
      setStep('otp');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError(t('misc_login.otp_6'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: otpError, response } = await apiClient.POST('/otp/login', {
        body: { phone, code: otp, full_name: fullName.trim() || undefined },
      });
      if (otpError) {
        if (response.status === 403 && isApiErrorMessage(otpError) && otpError.status === 'pending') {
          setResultMessage(PENDING_MESSAGE);
          setResultType('pending');
          setStep('result');
        } else if (response.status === 403) {
          setError(apiErrorMessage(otpError, BLOCKED_MESSAGE));
        } else if (response.status === 429) {
          setError(RATE_LIMITED_MESSAGE);
        } else {
          setError(t('misc_login.otp_invalid'));
        }
        return;
      }
      await refreshUser();
      if (data.action === 'registered_as_owner') {
        setResultMessage(t('phone_register.registered'));
        setResultType('success');
        setStep('result');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Email auth handlers
  const handleEmailLogin = async () => {
    if (!emailAddress || !emailPassword) {
      setError(t('misc_login.email_password_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const signedIn = await login(emailAddress, emailPassword);
      // Lovable: a platform admin lands in the admin panel.
      navigate(signedIn?.is_platform_admin && !signedIn.merchant_id ? '/admin/dashboard' : '/dashboard');
    } catch (err) {
      if (isApiErrorMessage(err)) {
        if (err.status === 'pending') {
          setResultMessage(PENDING_MESSAGE);
          setResultType('pending');
          setEmailStep('result');
        } else {
          setError(apiErrorMessage(err, BLOCKED_MESSAGE));
        }
        return;
      }
      setError(t('misc_login.bad_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async () => {
    if (!emailAddress || !emailPassword || !emailFullName.trim() || !merchantName.trim()) {
      setError(t('misc_login.fill_all'));
      return;
    }
    if (emailPassword.length < 8) {
      setError(t('misc_login.password_min'));
      return;
    }
    if (emailPassword !== emailPasswordConfirmation) {
      setError(t('misc_login.password_mismatch'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: registerError } = await apiClient.POST('/register', {
        body: {
          identifier: emailAddress,
          password: emailPassword,
          password_confirmation: emailPasswordConfirmation,
          full_name: emailFullName,
          merchant_name: merchantName,
        },
      });
      if (registerError) {
        setError(apiErrorMessage(registerError, t('misc_login.register_error_check')));
        return;
      }
      await login(emailAddress, emailPassword);
      navigate('/dashboard');
    } catch {
      setError(t('misc_login.register_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!emailAddress) {
      setError(t('misc_login.email_required'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: forgotError } = await apiClient.POST('/forgot-password', { body: { email: emailAddress } });
      if (forgotError) {
        setError(t('misc_login.email_format'));
        return;
      }
      setResultMessage(t('misc_login.reset_sent'));
      setResultType('success');
      setEmailStep('result');
    } finally {
      setLoading(false);
    }
  };

  const resetMode = () => {
    setError('');
    setStep('phone');
    setEmailStep('login');
    setOtp('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Hisob POS</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('misc_login.signin')}</p>
        </div>

        {/* Mode switcher */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setMode('email'); resetMode(); }}
            className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${mode === 'email' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-accent'}`}
          >
            <Mail size={14} className="inline mr-1.5" />
            Email
          </button>
          <button
            onClick={() => { setMode('phone'); resetMode(); }}
            className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${mode === 'phone' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-accent'}`}
          >
            <Phone size={14} className="inline mr-1.5" />
            {t('misc_login.phone')}
          </button>
        </div>

        {/* EMAIL MODE */}
        {mode === 'email' && (
          <div className="space-y-4">
            {emailStep === 'login' && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" placeholder="email@example.com" value={emailAddress} onChange={e => { setEmailAddress(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.password')}</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" placeholder="••••••" value={emailPassword} onChange={e => { setEmailPassword(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleEmailLogin} disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                  {t('misc_login.login')}
                </Button>
                <div className="flex items-center justify-between">
                  <button onClick={() => { setEmailStep('forgot'); setError(''); }} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {t('misc_login.forgot')}
                  </button>
                  <button onClick={() => { setEmailStep('register'); setError(''); }} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <span className="text-primary font-medium">{t('misc_login.register')}</span>
                  </button>
                </div>
              </>
            )}

            {emailStep === 'register' && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.full_name')}</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder={t('misc_login.full_name_ph')} value={emailFullName} onChange={e => { setEmailFullName(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.store_name')}</label>
                  <div className="relative">
                    <Store size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder={t('misc_login.store_name')} value={merchantName} onChange={e => { setMerchantName(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" placeholder="email@example.com" value={emailAddress} onChange={e => { setEmailAddress(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.password')}</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" placeholder={t('misc_login.password_min_ph')} value={emailPassword} onChange={e => { setEmailPassword(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.password_confirm')}</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="password" placeholder={t('misc_login.password_confirm_ph')} value={emailPasswordConfirmation} onChange={e => { setEmailPasswordConfirmation(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleEmailRegister} disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                  {t('misc_login.register')}
                </Button>
                <button onClick={() => { setEmailStep('login'); setError(''); }} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center">
                  {t('misc_login.back_to_login')}
                </button>
              </>
            )}

            {emailStep === 'forgot' && (
              <>
                <p className="text-sm text-muted-foreground text-center">{t('misc_login.forgot_hint')}</p>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" placeholder="email@example.com" value={emailAddress} onChange={e => { setEmailAddress(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleForgotPassword} disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                  {t('misc_login.send_link')}
                </Button>
                <button onClick={() => { setEmailStep('login'); setError(''); }} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center">
                  {t('misc_login.back_to_login')}
                </button>
              </>
            )}

            {emailStep === 'result' && (
              <div className={`p-4 rounded-xl border text-center space-y-2 ${
                resultType === 'success' ? 'bg-success/10 border-success/20' :
                resultType === 'pending' ? 'bg-warning/10 border-warning/20' :
                'bg-destructive/10 border-destructive/20'
              }`}>
                <p className="text-3xl">{resultType === 'success' ? '✅' : resultType === 'pending' ? '⏳' : '❌'}</p>
                <p className="text-sm text-foreground font-medium">{resultMessage}</p>
                {resultType !== 'success' && (
                  <Button variant="outline" size="sm" onClick={() => { setEmailStep('login'); setError(''); }}>
                    {t('misc_login.retry')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* PHONE MODE */}
        {mode === 'phone' && (
          <div className="space-y-4">
            {step === 'phone' && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.phone_number')}</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="tel" placeholder="+998901234567" value={phone} onChange={e => { setPhone(e.target.value); setError(''); }} className="h-11 pl-10" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('phone_register.full_name')}</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder={t('phone_register.full_name_placeholder')} value={fullName} onChange={e => setFullName(e.target.value)} className="h-11 pl-10" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('phone_register.hint')}</p>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleSendOtp} disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                  {t('misc_login.send_otp')}
                </Button>
              </>
            )}

            {step === 'otp' && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">{t('misc_login.enter_otp')}</label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input key="otp-code" autoFocus type="text" placeholder="123456" maxLength={6} value={otp} onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }} className="h-11 pl-10 text-center text-lg tracking-widest font-mono" />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button onClick={handleVerifyOtp} disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {t('misc_login.confirm')}
                </Button>
                <button onClick={() => { setStep('phone'); setOtp(''); }} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('misc_login.back')}
                </button>
              </>
            )}

            {step === 'result' && (
              <div className={`p-4 rounded-xl border text-center space-y-2 ${
                resultType === 'success' ? 'bg-success/10 border-success/20' :
                resultType === 'pending' ? 'bg-warning/10 border-warning/20' :
                'bg-destructive/10 border-destructive/20'
              }`}>
                <p className="text-3xl">{resultType === 'success' ? '✅' : resultType === 'pending' ? '⏳' : '❌'}</p>
                <p className="text-sm text-foreground font-medium">{resultMessage}</p>
                {resultType !== 'success' && (
                  <Button variant="outline" size="sm" onClick={() => { setStep('phone'); setOtp(''); setMode('phone'); }}>
                    {t('misc_login.retry')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
